if (!customElements.get('product-buy-box')) {
  customElements.define('product-buy-box', class ProductBuyBox extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.finePointer = window.matchMedia('(pointer: fine)');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.thumbItems = [...this.querySelectorAll('[data-gallery-thumbs] [data-gallery-index]')];
      this.cursor = this.querySelector('[data-gallery-cursor]');
      this.activeGalleryIndex = 0;
      this.form = this.querySelector('[data-buy-box-form]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.price = this.querySelector('[data-current-price]');
      this.compare = this.querySelector('[data-compare-price]');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-form-error]');
      this.quantityInput = this.querySelector('[data-buy-box-quantity]');
      this.quantityDecreaseButton = this.querySelector('[data-quantity-decrease]');
      this.quantityIncreaseButton = this.querySelector('[data-quantity-increase]');
      this.variants = this.readJSON('[data-variant-data]') || [];

      const signal = this.abortController.signal;

      this.thumbItems.forEach((thumb) => thumb.addEventListener('click', () => {
        this.goTo(Number(thumb.dataset.galleryIndex));
      }, { signal }));

      if (this.viewport && this.slides.length > 1) {
        this.pendingProgrammaticScroll = false;
        let frame;
        this.viewport.addEventListener('scroll', () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.onViewportScroll());
        }, { passive: true, signal });
        this.viewport.addEventListener('scrollend', () => this.onViewportScrollEnd(), { signal });

        if (this.stage) {
          this.stage.classList.add('is-navigable');
          this.stage.addEventListener('pointermove', (event) => this.moveCursor(event), { passive: true, signal });
          this.stage.addEventListener('pointerleave', () => this.hideCursor(), { signal });
          this.stage.addEventListener('click', (event) => this.onStageClick(event), { signal });
        }

        window.addEventListener('resize', () => this.centerActiveThumb(false), { passive: true, signal });
      }

      this.form?.addEventListener('submit', (event) => this.addToCart(event), { signal });
      this.bindQuantityControls(signal);
      this.bindVariantUpdates();
      this.setActive(0);
    }

    disconnectedCallback() {
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      clearTimeout(this.scrollSettleTimer);
      this.abortController?.abort();
    }

    readJSON(selector) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || 'null'); }
      catch (_) { return null; }
    }

    goTo(index) {
      const targetIndex = Math.max(0, Math.min(Number(index), this.slides.length - 1));
      const slide = this.slides[targetIndex];
      if (!slide || !this.viewport) return;
      // Mark the upcoming smooth scroll as programmatic so the thumbnail strip does not
      // chase every in-between slide while the main image animates toward its target.
      this.pendingProgrammaticScroll = true;
      this.deferScrollSettle();
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      this.setActive(targetIndex);
    }

    onViewportScroll() {
      // While a programmatic (click/cursor/variant) scroll is running, ignore the
      // intermediate positions so the thumbnails settle straight to the target.
      if (this.pendingProgrammaticScroll) {
        this.deferScrollSettle();
        return;
      }
      const index = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      if (index !== this.activeGalleryIndex) this.setActive(index);
    }

    onViewportScrollEnd() {
      clearTimeout(this.scrollSettleTimer);
      this.pendingProgrammaticScroll = false;
      this.setActive(Math.round(this.viewport.scrollLeft / this.viewport.clientWidth));
    }

    deferScrollSettle() {
      clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = setTimeout(() => this.onViewportScrollEnd(), 120);
    }

    setActive(index) {
      const targetIndex = Math.max(0, Math.min(Number(index), this.slides.length - 1));
      const changed = targetIndex !== this.activeGalleryIndex || !this.galleryInitialized;
      this.activeGalleryIndex = targetIndex;
      this.galleryInitialized = true;
      // Mirrors PageDots.select from the reference theme: always keep the selected
      // thumbnail centered, and only refresh state when the index actually changes.
      this.centerActiveThumb(true);
      if (!changed) return;
      this.thumbItems.forEach((thumb) => {
        thumb.setAttribute('aria-current', Number(thumb.dataset.galleryIndex) === targetIndex ? 'true' : 'false');
      });
    }

    centerActiveThumb(animate) {
      if (!this.thumbs) return;
      const thumb = this.thumbItems.find((item) => Number(item.dataset.galleryIndex) === this.activeGalleryIndex);
      if (!thumb) return;
      const behavior = animate && window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto';
      this.thumbs.scrollTo({ left: thumb.offsetLeft - this.thumbs.clientWidth / 2 + thumb.clientWidth / 2, behavior });
    }

    moveCursor(event) {
      if (!this.cursor || !this.stage || !this.finePointer.matches) return;
      const rect = this.stage.getBoundingClientRect();
      const isStartHalf = event.clientX < rect.left + rect.width / 2;
      this.cursor.classList.toggle('is-half-start', isStartHalf);
      this.cursor.classList.toggle('is-half-end', !isStartHalf);
      this.cursor.classList.add('is-visible');
      const x = (event.clientX - rect.left - this.cursor.clientWidth / 2).toFixed(2);
      const y = (event.clientY - rect.top - this.cursor.clientHeight / 2).toFixed(2);
      this.cursor.style.translate = `${x}px ${y}px`;
    }

    hideCursor() {
      this.cursor?.classList.remove('is-visible', 'is-half-start', 'is-half-end');
    }

    onStageClick(event) {
      if (!this.stage || !this.finePointer.matches || this.slides.length < 2) return;
      const rect = this.stage.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      this.goTo(offsetX > rect.width / 2 ? this.activeGalleryIndex + 1 : this.activeGalleryIndex - 1);
    }

    bindVariantUpdates() {
      if (this.dataset.syncCurrentProduct !== 'true') return;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.variant) this.selectVariant(event.data.variant.id);
        });
      }

      document.addEventListener('change', (event) => {
        const input = event.target.closest('product-info input[name="id"], main-product input[name="id"]');
        if (input?.value) this.selectVariant(input.value);
      }, { signal: this.abortController?.signal });
    }

    selectVariant(id) {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;
      if (this.variantInput) this.variantInput.value = variant.id;
      if (this.price) this.price.textContent = variant.price;
      if (this.compare) {
        if (variant.compare_price) {
          this.compare.textContent = variant.compare_price;
          this.compare.hidden = false;
        } else {
          this.compare.hidden = true;
          this.compare.textContent = '';
        }
      }
      if (this.button) {
        this.button.disabled = !variant.available;
        this.button.textContent = variant.available ? this.button.dataset.availableText : this.button.dataset.soldOutText;
      }
      if (variant.media_index !== null && variant.media_index !== undefined) this.goTo(variant.media_index);
    }

    bindQuantityControls(signal) {
      this.quantityDecreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() - 1);
      }, { signal });
      this.quantityIncreaseButton?.addEventListener('click', () => {
        this.setQuantity(this.getQuantity() + 1);
      }, { signal });
      this.quantityInput?.addEventListener('change', () => {
        this.setQuantity(this.quantityInput.value);
      }, { signal });
      window.addEventListener('east-oak:product-quantity-change', (event) => {
        if (event.detail?.source === this) return;
        this.setQuantity(event.detail?.quantity, { silent: true });
      }, { signal });

      const syncedQuantity = Number.parseInt(window.EastOakSyncedProductQuantity, 10);
      this.setQuantity(Number.isFinite(syncedQuantity) ? syncedQuantity : this.getQuantity(), { silent: true });
    }

    getQuantity() {
      const value = Number.parseInt(this.quantityInput?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    setQuantity(value, options = {}) {
      if (!this.quantityInput) return;
      const quantity = Math.max(1, Number.parseInt(value, 10) || 1);
      this.quantityInput.value = quantity;
      if (this.quantityDecreaseButton) this.quantityDecreaseButton.disabled = quantity <= 1;
      if (!options.silent) this.publishQuantity(quantity);
    }

    publishQuantity(quantity) {
      window.EastOakSyncedProductQuantity = quantity;
      window.dispatchEvent(new CustomEvent('east-oak:product-quantity-change', {
        detail: { quantity, source: this },
      }));
    }

    async addToCart(event) {
      event.preventDefault();
      if (!this.form || !this.button || this.button.disabled) return;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;

      try {
        const response = await fetch(this.form.action + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
        window.location.assign(this.form.dataset.cartUrl);
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  });
}
